import { Box, Button, Flex, FlexList, useI18nContext } from "@procore/core-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
   ALLOCATION_TYPE_OPTIONS,
   FieldMappings,
   type AssignmentListDataTableProps,
   type AssignmentListServerFilter,
} from "./assignment-list-prop-types";
import { useProjectTearsheet } from "../tearsheets/project/project-tearsheet";
import { usePersonTearsheet } from "../tearsheets/people/people-tearsheet";
import {
   columnHeadersMap,
   convertAssignmentDataTableFilters,
   defaultAssignmentListTableConfig,
   filterFieldMaps,
   filterNameMaps,
   getSelectedDay,
   mapApiAssignmentToTableAssignment,
} from "./assignment-list-helpers";
import type {
   BooleanCellColumnDefinition,
   ColumnDefinition,
   DataTableCellRendererProps,
   DataTableConfig,
   DateCellColumnDefinition,
   SelectCellColumnDefinition,
   ServerSideGetRowsParams,
   TextCellColumnDefinition,
   ValueValidatorParams,
} from "@procore/data-table";
import {
   BooleanCellRenderer,
   DateCellEditor,
   DateCellRenderer,
   MultiSelectFilterRenderer,
   PercentCellEditor,
   PercentCellRenderer,
   SelectCellEditor,
   SelectCellRenderer,
   ServerSideDataTable,
   TextCellRenderer,
} from "@procore/data-table";
import { CustomLinkCellRenderer } from "../data-table/custom-link-column/custom-link-cell";
import { DataTableToolbarQuickFilters } from "../data-table/DataTableToolbarQuickFilters";
import type {
   CustomField,
   FormattedOption,
   IntegratedField,
   SelectValue,
   SortModel,
} from "@/react/prop-types";
import { ResourceType, EntityTypes, PageTitle } from "@/react/prop-types";
import {
   convertDataTableConfigToSavedView,
   formatDateTime,
   formatOptions,
   getReorderedColumnDefinition,
} from "@/react/shared/helper";
import {
   ColorSelectEditor,
   ColorSelectRenderer,
} from "../data-table/ColorSelectComponent/ColorSelectColumn";
import { DateFilter } from "../data-table/custom-filters/date-filter";
import {
   customFilterTokenText,
   generateProjectRoleColumnDefinitions,
   getDateFilterTokenText,
} from "../project-list/helpers";
import { TextFilter } from "../data-table/custom-filters/text-filter";
import { AuthAction, usePermissionContext } from "@/react/providers/permission-context-provider";
import { authManager } from "@/lib/managers/auth-manager";
import {
   createColumnDefinitionsFromCustomHeaders,
   findCustomFieldByIntegrationName,
} from "@/react/shared/custom-field-utils";
import {
   useGetCustomFields,
   useGetIntegratedFields,
   useGetProjectStages,
} from "../common/queries/queries";
import { renderHeaderNode as renderProjectHeaderNode } from "../project-list/project-list-data-table";
import { renderHeaderNode as renderPeopleHeaderNode } from "../people-list/people-list-data-table";
import { StatusFilter } from "../data-table/custom-filters/status-filter";
import { NumericValueFilter } from "../data-table/custom-filters/numeric-value-filter";
import { BooleanFilter } from "../data-table/custom-filters/bool-filter";
import { CheckboxFilterRenderer } from "../data-table/custom-filters/checkbox-filter";
import { PEOPLE_STATUS_OPTIONS } from "../people-list/people-list-prop-types";
import type { CustomFieldType } from "@laborchart-modules/common";
import { ConfirmDeleteAssignmentModal } from "@/react/shared/modals/confirm-delete-assignment-modal";
import { BulkEditAssignmentTearsheet } from "../tearsheets/assignment/bulk-edit-assignment-tearsheet";
import { CustomFieldEntity } from "@laborchart-modules/common/dist/rethink/schemas/enums/custom-fields";
import { ProjectRolesFilter } from "../data-table/custom-filters/project-roles-filter";
import { AssignmentListQuickControls } from "./assignment-list-quick-controls";
import { ConfirmDeleteModal } from "@/react/shared/modals/confirm-delete-modal";
import { Assignment2Store } from "@/stores/assignment-2-store.core";
import {
   onEditAssignmentClick,
   useAssignmentTearsheet,
} from "../tearsheets/assignment/assignment-tearsheet";
import { Pencil } from "@procore/core-icons";
import { DateUtils } from "@/lib/utils/date";
import { CustomResourceCellRenderer } from "../data-table/custom-resource-cell-renderer/custom-resource-cell-renderer";
import {
   haveFiltersChanged,
   prepareConfigForStorage,
   type ListServerFilter,
} from "../data-table/data-table-utils";
import { CreateSavedViewModal } from "../modals/saved-view/create-saved-view-modal";
import { SavedViewPage } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import { filterRendererMap } from "./assignment-list-filter-renderer-map";
import { AssignmentListSendAlert } from "./assignment-list-send-alert";
import { getOrderedTimeOptions } from "../tearsheets/time-off/helpers";
import { DEFAULT_START_TIME, PROJECT_STATUS_OPTIONS } from "@/react/shared/constants";
import { useToastAlertContext } from "@procore/toast-alert";
import { BulkActionsBar } from "../data-table/BulkActionsBar";
import { EquipmentAssignmentStore } from "@/stores/equipment-assignments-store.core";
import { useViewableStatusesOptions } from "./queries";

const LOCAL_STORAGE_CONFIG_KEY = "assignmentListTableConfig";
const LOCAL_STORAGE_SAVED_VIEW_KEY = "assignmentListSavedViewConfig";

export const getProjectString = (I18n: ReturnType<typeof useI18nContext>) => {
   return I18n.t("views.company.workforce_planning.project");
};

export const updateCustomFieldItems = (
   items: Array<{ field: string; [key: string]: any }>,
   customFields: CustomField[],
   includeFieldType = false,
) => {
   const enumValues = Object.values(EntityTypes).join("|");
   // this will match pattern like Assignment's custom_field, Project's custom_field, People's custom_field
   const regex = new RegExp(`\\b(${enumValues})'\\s?s\\s+(.+)\\b`, "i");
   // loop through the items and update the fieldId and entity properties
   items.forEach((item) => {
      /*this matching is required as we are maintaining custom fields based on entity types 
         and the server side data table returns the field value, based on how we map them.
       */
      const match = regex.exec(item.field);
      if (match) {
         const type = match[1]; // Capturing group contains the matched type
         const field = match[2]; // Capturing group contains the matched field
         const enumKey = type.toUpperCase() as keyof typeof EntityTypes;
         const customField = findCustomFieldByIntegrationName(customFields, field);
         if (customField) {
            item.fieldId = customField.id;
            item.entity = EntityTypes[enumKey]; // this can later be used for switching the sort_by/property attribute in API
            if (includeFieldType) {
               item.fieldType = customField.type as CustomFieldType;
            }
         }
      }
   });
};

/* istanbul ignore next */
export const AssignmentListDataTable = ({
   tableApi,
   fetchAssignmentList,
   handleTableReady,
   groupId,
   streamJobTitles,
   streamViewableStatuses,
   streamPeople,
   streamProjects,
   customFields,
   roleOptions,
   isLastNameFirst,
   savedView,
   assignmentCreationSupportData,
   updateAssignment,
}: AssignmentListDataTableProps) => {
   const I18n = useI18nContext();
   const [customColumnDefinitions, setCustomColumnDefinitions] = useState<ColumnDefinition[]>([]);
   const { checkAuthAction } = usePermissionContext();
   const { data: integratedFields, isLoading: integratedFieldsIsLoading } =
      useGetIntegratedFields();
   const [canViewPeopleSensitiveFields, setCanViewPeopleSensitiveFields] = useState(false);
   const [canViewPeopleFinancials, setCanViewPeopleFinancials] = useState(false);
   const [peopleSensitiveFields, setPeopleSensitiveFields] = useState<string[]>([]);
   const [canViewProjectSensitiveFields, setCanViewProjectSensitiveFields] = useState(false);
   const [canViewProjectFinancials, setCanViewProjectFinancials] = useState(false);
   const [projectSensitiveFields, setProjectSensitiveFields] = useState<string[]>([]);
   const [projectIntegratedFields, setProjectIntegratedFields] = useState<IntegratedField[]>([]);
   const [peopleIntegratedFields, setPeopleIntegratedFields] = useState<IntegratedField[]>([]);
   const [canManageAssignment, setCanManageAssignment] = useState(false);
   const [canViewProjectRoles, setCanViewProjectRoles] = useState(false);
   const { dispatch: projectTearsheetDispatch } = useProjectTearsheet();
   const { dispatch: personTearsheetDispatch } = usePersonTearsheet();
   const [manuallySelectedRowData, setManuallySelectedRowData] = useState<any>([]);
   const [selectionInProgress, setSelectionInProgress] = useState(false);
   const { showToast } = useToastAlertContext();
   //refreshCustomFields is maintained to trigger customFields useEffect when component rerenders on some event like create tearsheet button clicked
   const [refreshCustomFields, setRefreshCustomFields] = useState(false);

   const orderedTimeOptions = getOrderedTimeOptions(DEFAULT_START_TIME);

   // in bulk edit assignment we only need to use the custom fields for assignment
   const { data: assignmentCustomFields } = useGetCustomFields(CustomFieldEntity.ASSIGNMENT);
   const { data: viewableStatusesOptions } = useViewableStatusesOptions(groupId);

   const { data: projectStages } = useGetProjectStages();

   // Next starting_after value for pagination
   const nextStartAfter = useRef(undefined);
   // reading tableConfig like this to avoid re-renders for server side filters
   // they kind of reset when re-rendered
   const cachedConfig =
      JSON.parse(localStorage.getItem(LOCAL_STORAGE_CONFIG_KEY)!) ??
      defaultAssignmentListTableConfig;

   const tableConfig = savedView ?? cachedConfig;

   // Track previous filters to detect actual filter changes (vs other config changes)
   const previousFiltersRef = useRef<ListServerFilter[]>(tableConfig.serverFilters ?? []);

   /*unique key formed to pass it to DataTable Component for remounting of component when needed
      this is required if filters are applied and someone switches the group from navigation bar
      or when create tearsheet or project, people hyperlink is clicked on the table cell, it cause re-rendering so remounting
      of DataTable component is needed for persisting with filters*/
   const dataTableKey = groupId;

   useEffect(() => {
      if (integratedFields) {
         if (Array.isArray(integratedFields.data.projects_integrated_fields)) {
            setProjectIntegratedFields(integratedFields.data.projects_integrated_fields);
         }
         if (Array.isArray(integratedFields.data.people_integrated_fields)) {
            const updatedFields = integratedFields.data.people_integrated_fields.map((field) => {
               switch (field.property) {
                  case "first_name":
                  case "last_name":
                     return { ...field, property: "person" };
                  case "is_male":
                     return { ...field, property: "gender" };
                  default:
                     return field;
               }
            });

            setPeopleIntegratedFields(updatedFields);
         }
      }
   }, [integratedFields]);

   useEffect(() => {
      //people
      const canViewPeopleSensitiveFields = checkAuthAction(AuthAction.VIEW_PEOPLE_SENSITIVE);
      setCanViewPeopleSensitiveFields(canViewPeopleSensitiveFields);
      const canViewPeopleFinancials = checkAuthAction(AuthAction.VIEW_PEOPLE_FINANCIALS);
      setCanViewPeopleFinancials(canViewPeopleFinancials);
      setPeopleSensitiveFields(
         authManager
            .peopleSensitiveFields()
            .map((field) => (field == "is_male" ? "gender" : field)),
      );

      //projects
      const canViewProjectSensitiveFields = checkAuthAction(AuthAction.VIEW_PROJECT_SENSITIVE);
      setCanViewProjectSensitiveFields(canViewProjectSensitiveFields);
      setProjectSensitiveFields(authManager.projectsSensitiveFields());
      const canViewProjectFinancials = checkAuthAction(AuthAction.VIEW_PROJECT_FINANCIALS);
      setCanViewProjectFinancials(canViewProjectFinancials);
      const canViewProjectRoles = checkAuthAction(AuthAction.VIEW_PROJECT_ROLES);
      setCanViewProjectRoles(canViewProjectRoles);

      //assignments
      const canManageAssignment = checkAuthAction(AuthAction.MANAGE_ASSIGNMENTS);
      setCanManageAssignment(canManageAssignment);
   }, [checkAuthAction]);

   //If the Group ID prop changes, fetch the assignment list again
   useEffect(() => {
      if (tableApi) {
         tableApi.refreshServerSide({});
      }
   }, [groupId]);

   const onTableConfigChange = useCallback(
      (config: DataTableConfig) => {
         const preparedConfig = prepareConfigForStorage(
            config,
            customFields,
            customColumnDefinitions,
         );
         if (!savedView) {
            localStorage.setItem(LOCAL_STORAGE_CONFIG_KEY, JSON.stringify(preparedConfig));
         } else {
            localStorage.setItem(LOCAL_STORAGE_SAVED_VIEW_KEY, JSON.stringify(preparedConfig));
         }
      },
      [savedView, customFields, customColumnDefinitions],
   );

   /* istanbul ignore next */
   const Delete = (props: DataTableCellRendererProps) => {
      const current_day = DateUtils.getDetachedDay(new Date());

      let completed = false;
      let inProgress = false;

      const { start_day, end_day, assignment_type } = props.data;

      // Check in-progress first
      if (current_day > start_day && current_day < end_day) {
         inProgress = true;
      } else if (current_day > end_day) {
         completed = true;
      }

      const isDeleteDisabled = completed || inProgress;
      const onDeleteStore =
         assignment_type === "labor"
            ? Assignment2Store.deleteAssignment
            : EquipmentAssignmentStore.deleteEquipmentAssignment;
      return (
         <ConfirmDeleteModal
            id={props.data.id}
            tableApi={tableApi}
            headerText={"views.company.workforce_planning.assignment.modal.delete.header"}
            deleteText={"views.company.workforce_planning.assignment.modal.delete.description"}
            successText={"views.company.workforce_planning.assignment.modal.delete.success"}
            errorText={"views.company.workforce_planning.assignment.modal.delete.error"}
            onDelete={onDeleteStore}
            isDeleteDisabled={isDeleteDisabled}
            store={Assignment2Store}
         />
      );
   };

   const { dispatch: assignmentTearsheetDispatch } = useAssignmentTearsheet();

   const Edit = (props: DataTableCellRendererProps) => {
      if (!props || !props.data) {
         console.warn("Edit component rendered without props or props.data");
         return null; // Render nothing if props are missing
      }

      const { id, assignment_type } = props.data;

      const handleEdit = () => {
         setRefreshCustomFields(!refreshCustomFields);
         onEditAssignmentClick(assignmentTearsheetDispatch, id, assignment_type);
      };

      return (
         <Button
            aria-label="edit"
            icon={<Pencil />}
            onClick={() => handleEdit()}
            variant="tertiary"
         />
      );
   };

   // This is in place to allow saved views with project search filters to work
   // This is hidden via display:none in ./style.css
   const projectSearchColumn: ColumnDefinition = {
      field: "project_search",
      headerName: I18n.t("views.company.workforce_planning.project"),
      hidden: true,
      lockVisible: true,
      filterRenderer: TextFilter as any,
   };

   // DataTable Column definitions
   const projectNameColumn: ColumnDefinition = {
      field: "project",
      headerName: I18n.t("views.company.workforce_planning.project"),
      cellRenderer: CustomLinkCellRenderer,
      cellRendererParams: {
         pageTitle: PageTitle.PROJECT_LIST,
         getProjectDetailDispatch: projectTearsheetDispatch,
         refreshCustomFields: () => setRefreshCustomFields(!refreshCustomFields),
      },
      getStringFormattedValue: (value) => value,
      editable: false,
      lockVisible: true,
      filterRenderer: MultiSelectFilterRenderer,
      filterProps: {
         index: 0,
         getLabel: (item: FormattedOption) => item.label,
         getFilterOptions: async () => await streamProjects(groupId),
      },
      columnHeaderParams: {
         headerNode: () => renderProjectHeaderNode(projectIntegratedFields, "name"),
      },
      minWidth: 250,
   };

   const peopleNameColumn: ColumnDefinition = {
      field: "resource",
      headerName: I18n.t("views.company.workforce_planning.people.name"),
      cellRenderer: CustomResourceCellRenderer,
      cellRendererParams: {
         getPersonDetailDispatch: personTearsheetDispatch,
         refreshCustomFields: () => setRefreshCustomFields(!refreshCustomFields),
      },
      editable: false,
      lockVisible: true,
      filterRenderer: MultiSelectFilterRenderer,
      filterProps: {
         index: 1,
         getLabel: (item: FormattedOption) => item.label,
         getFilterOptions: async () => await streamPeople(groupId),
      },
      columnHeaderParams: {
         headerNode: () => renderPeopleHeaderNode(peopleIntegratedFields, "person"),
      },
      minWidth: 250,
   };

   const projectNumberColumn: TextCellColumnDefinition = {
      field: "job_number",
      cellRenderer: TextCellRenderer,
      headerName: I18n.t("views.company.workforce_planning.projects.project_number"),
      editable: false,
      columnHeaderParams: {
         headerNode: () => renderProjectHeaderNode(projectIntegratedFields, "job_number"),
      },
      minWidth: 200,
   };

   const jobTitleColumn: ColumnDefinition = {
      field: "job_title",
      cellRenderer: ColorSelectRenderer,
      cellRendererParams: {
         getColor: (item: { color: string }) => item.color,
         getShape: (item: { shape: string }) => item.shape,
      },
      getStringFormattedValue: (item) => item.label,
      headerName: I18n.t("views.company.workforce_planning.people.job_title"),
      editable: false,
      filterRenderer: MultiSelectFilterRenderer,
      filterProps: {
         index: 2,
         getLabel: (item: FormattedOption) => item.label,
         getFilterOptions: async () => await streamJobTitles(groupId),
      },
      columnHeaderParams: {
         headerNode: () => renderPeopleHeaderNode(peopleIntegratedFields, "position_id"),
      },
      minWidth: 200,
   };

   const startDateColumn: DateCellColumnDefinition = {
      field: "start_date",
      headerName: I18n.t("views.company.workforce_planning.assignment.start_date"),
      cellRenderer: DateCellRenderer,
      cellRendererParams: {
         timeZone: "UTC",
      },
      filterProps: {
         getFilterTokenText: (item: any) => getDateFilterTokenText(item),
         index: 4,
      },
      filterRenderer: DateFilter,
      minWidth: 150,
      editable: true,
      cellEditor: DateCellEditor,
      cellEditorParams: {
         timeZone: "UTC",
      },
      valueValidator: (value) => {
         const { value: startDate, data } = value;

         // Check if the start date is empty
         if (!startDate) {
            return {
               isValid: false,
               errorMessage: I18n.t("views.company.workforce_planning.validations.required_field"),
               isRequired: true,
            };
         }

         // Check if the end date is earlier than the start date
         if (data?.end_date && data.end_date < data.start_date) {
            return {
               isValid: false,
               errorMessage: I18n.t(
                  "views.company.workforce_planning.assignment.bulk.edit.validation.start_date",
                  {
                     date: formatDateTime(data.end_date as Date, {
                        locale: I18n.locale,
                        isRequiredTimezone: false,
                     }),
                  },
               ),
               isRequired: true,
            };
         }

         // Check if the selected day is valid
         const selectedDay =
            startDate instanceof Date && !isNaN(startDate.getTime())
               ? getSelectedDay(startDate, data?.work_days_object)
               : null;
         if (!selectedDay) {
            return {
               isValid: false,
               errorMessage: I18n.t(
                  "views.company.workforce_planning.assignment.bulk.edit.validation.start_date_active_day",
               ),
               isRequired: true,
            };
         }

         return { isValid: true, isRequired: true };
      },
      onCellValueChanged: async (params) => {
         if (!params.newValue) return;

         const { newValue, rowData } = params;
         const startDate = newValue || rowData.start_date;
         const endDate = rowData.end_date;

         // Check if start date is greater than end date
         if (endDate?.getTime() < startDate?.getTime()) return;

         // Check if the selected start date is a valid work day
         const selectedDay = getSelectedDay(startDate, rowData?.work_days_object);
         if (!selectedDay) return;

         // Format the date to UTC before sending to the server because we store dates in UTC format
         // Also, we are getting -1 day issue if we don't format it to UTC here
         const formattedDate =
            newValue instanceof Date
               ? new Date(
                    formatDateTime(newValue, {
                       locale: "en",
                       isRequiredTimezone: true,
                       timeZone: "UTC",
                    }),
                 )
               : undefined;

         params.newValue = formattedDate;

         // Update the assignment
         await updateAssignment(params);
      },
   };

   const endDateColumn: DateCellColumnDefinition = {
      field: "end_date",
      headerName: I18n.t("views.company.workforce_planning.assignment.end_date"),
      cellRenderer: DateCellRenderer,
      cellRendererParams: {
         timeZone: "UTC",
      },
      filterProps: {
         getFilterTokenText: (item: any) => getDateFilterTokenText(item),
         index: 5,
      },
      filterRenderer: DateFilter,
      minWidth: 150,
      editable: true,
      cellEditor: DateCellEditor,
      cellEditorParams: {
         timeZone: "UTC",
      },
      valueValidator: (value) => {
         const { value: endDate, data } = value;
         if (!endDate) {
            return {
               isValid: false,
               errorMessage: I18n.t("views.company.workforce_planning.validations.required_field"),
               isRequired: true,
            };
         }
         // Check if the end date is before the start date or not - value is an object with value and data properties
         // Check if the end date is empty or not
         if (data?.end_date != null && data?.end_date < data?.start_date) {
            return {
               isValid: false,
               errorMessage: I18n.t(
                  "views.company.workforce_planning.validations.end_date_before_start_date",
               ),
               isRequired: true,
            };
         }
         const selectedDay =
            endDate instanceof Date && !isNaN(endDate.getTime())
               ? getSelectedDay(endDate, data?.work_days_object)
               : null;
         if (!selectedDay) {
            return {
               isValid: false,
               errorMessage: I18n.t(
                  "views.company.workforce_planning.assignment.bulk.edit.validation.end_date_active_day",
               ),
               isRequired: true,
            };
         }

         return { isValid: true };
      },
      onCellValueChanged: async (params) => {
         if (!params.newValue) return;

         const { newValue, rowData } = params;
         const startDate = rowData.start_date;
         const endDate = newValue || rowData.end_date;

         // Check if start date is greater than end date
         if (endDate?.getTime() < startDate?.getTime()) return;

         // Check if the selected start date is a valid work day
         const selectedDay = getSelectedDay(endDate, rowData?.work_days_object);
         if (!selectedDay) return;

         // Format the date to UTC before sending to the server because we store dates in UTC format
         // Also, we are getting -1 day issue if we don't format it to UTC here
         const formattedDate =
            newValue instanceof Date
               ? new Date(
                    formatDateTime(newValue, {
                       locale: "en",
                       isRequiredTimezone: true,
                       timeZone: "UTC",
                    }),
                 )
               : undefined;

         params.newValue = formattedDate;

         // Update the assignment
         await updateAssignment(params);
      },
   };

   const startTimeColumn: SelectCellColumnDefinition = {
      field: "start_time",
      cellRenderer: SelectCellRenderer,
      cellRendererParams: {
         tooltipParams: ({ data }: { data: any }) => {
            if (data.percent_allocated) {
               return {
                  trigger: "hover",
                  showDelay: 400,
                  placement: "top",
                  hideDelay: 1000,
                  overlay: I18n.t(
                     "views.company.workforce_planning.assignment.validations.start_time_non_editable",
                  ),
               };
            }
         },
      },
      headerName: I18n.t("views.company.workforce_planning.assignment.start_time"),
      minWidth: 150,
      editable: (data) => !data.data.percent_allocated,
      cellEditor: SelectCellEditor,
      cellEditorParams: {
         selectOptions: () => orderedTimeOptions,
         getId: (item: SelectValue<any>) => item?.label,
      },
      getStringFormattedValue: (value: any) => value?.label ?? "--",
      valueValidator: () => ({ isValid: true, isRequired: true }),
   };

   const endTimeColumn: SelectCellColumnDefinition = {
      field: "end_time",
      cellRenderer: SelectCellRenderer,
      cellRendererParams: {
         tooltipParams: ({ data }: { data: any }) => {
            if (data.percent_allocated) {
               return {
                  trigger: "hover",
                  showDelay: 400,
                  placement: "top",
                  hideDelay: 1000,
                  overlay: I18n.t(
                     "views.company.workforce_planning.assignment.validations.end_time_non_editable",
                  ),
               };
            }
         },
      },
      headerName: I18n.t("views.company.workforce_planning.assignment.end_time"),
      minWidth: 150,
      editable: (data) => !data.data.percent_allocated,
      cellEditor: SelectCellEditor,
      cellEditorParams: {
         selectOptions: () => orderedTimeOptions,
         getId: (item: SelectValue<any>) => item?.label,
      },
      getStringFormattedValue: (value: any) => value?.label ?? "--",
      valueValidator: () => ({ isValid: true, isRequired: true }),
   };

   const percentAllocatedColumn: ColumnDefinition = {
      field: "percent_allocated",
      cellRenderer: PercentCellRenderer,
      cellRendererParams: {
         tooltipParams: (data: any) => {
            if (data.data.start_time && data.data.end_time) {
               return {
                  trigger: "hover",
                  showDelay: 400,
                  placement: "top",
                  hideDelay: 1000,
                  overlay: I18n.t(
                     "views.company.workforce_planning.assignment.validations.percent_allocation_non_editable",
                  ),
               };
            }
         },
      },
      filterRenderer: NumericValueFilter,
      filterProps: {
         getFilterHeadingText: () =>
            I18n.t("views.company.workforce_planning.assignment_percent_allocated"),
         getFilterTokenText: (item: any) => {
            return customFilterTokenText(
               item,
               "assignment_percent_allocated",
               I18n,
               "views.company.workforce_planning.",
            );
         },
         index: 7,
      },
      headerName: I18n.t("views.company.workforce_planning.assignment.percent_allocated"),
      editable: (data) => !(data.data.start_time && data.data.end_time),
      cellEditor: PercentCellEditor,
      rightAlign: true,
      minWidth: 200,
      valueValidator: (params: ValueValidatorParams) => {
         if (
            params.data?.percent_allocated <= 0 &&
            !params.data?.start_time &&
            !params.data?.end_time
         ) {
            return {
               isValid: false,
               errorMessage: I18n.t(
                  "views.company.workforce_planning.assignment.bulk.edit.validation.percent_allocation_greater_than_0",
               ),
               isRequired: true,
            };
         }

         return { isValid: true, isRequired: true };
      },
      onCellValueChanged: async (params) => {
         if (
            params.field === FieldMappings.PERCENT_ALLOCATED &&
            (isNaN(Number(params.newValue)) || Number(params.newValue) <= 0)
         )
            return;
         await updateAssignment(params);
      },
   };

   const statusColumn: ColumnDefinition = {
      field: "status",
      cellRenderer: ColorSelectRenderer,
      cellRendererParams: {
         getColor: (item: { color: string }) => item.color,
         getShape: (item: { shape: string }) => item.shape,
      },
      getStringFormattedValue: (item) => item.label,
      headerName: I18n.t("views.company.workforce_planning.people.status"),
      filterRenderer: MultiSelectFilterRenderer,
      filterProps: {
         index: 3,
         getLabel: (item: FormattedOption) => item.label,
         getFilterOptions: async () => await streamViewableStatuses(),
      },
      columnHeaderParams: {
         headerNode: () => renderPeopleHeaderNode(peopleIntegratedFields, "status"),
      },
      minWidth: 200,
      editable: true,
      cellEditor: ColorSelectEditor,
      cellEditorParams: {
         getColor: (item: { color: string }) => item.color,
         options: viewableStatusesOptions,
         getOptions: (item: any) => item,
      },
   };

   const overtimeColumn: BooleanCellColumnDefinition = {
      field: "overtime",
      cellRenderer: BooleanCellRenderer,
      headerName: I18n.t("views.company.workforce_planning.assignment.overtime"),
      editable: false,
      sortable: false,
      filterRenderer: BooleanFilter as any,
      filterProps: {
         getFilterHeadingText: () => I18n.t("views.company.workforce_planning.assignment_overtime"),
         getFilterTokenText: (item: any) => {
            const text = I18n.t("views.company.workforce_planning.assignment_overtime") + ": ";
            const booleanValue: string[] = item.value.map((v: any) => {
               const booleanValue = v.value ? "yes_value" : "no_value";
               return I18n.t("views.company.workforce_planning.boolean_values." + booleanValue);
            });
            return text + booleanValue.join(", ");
         },
         index: 8,
      },
      minWidth: 200,
   };

   const categoryColumn: SelectCellColumnDefinition = {
      field: "cost_code_name",
      cellRenderer: SelectCellRenderer,
      cellRendererParams: {
         tooltipParams: ({ data }: { data: any }) => {
            if (!data.cost_code_name?.label) {
               return {
                  trigger: "hover",
                  showDelay: 400,
                  placement: "top",
                  hideDelay: 1000,
                  overlay: I18n.t(
                     "views.company.workforce_planning.assignment.validations.category_unavailable_check",
                  ),
               };
            }
         },
      },
      headerName: I18n.t("views.company.workforce_planning.category"),
      editable: (data) => data.data.categoryOptions.length > 0,
      minWidth: 200,
      cellEditor: SelectCellEditor,
      cellEditorParams: {
         selectOptions: async (data: any) => data.categoryOptions,
         getId: (item: { id: SelectValue<any> }) => item?.id,
         clearable: true,
      },
      getStringFormattedValue: (value) => value?.label ?? "",
      valueSetter: ({ data, newValue }) => {
         Object.assign(data, {
            cost_code_name: newValue,
            label_name: null,
            category_id: newValue?.id ?? null,
            subcategory_id: null,
            subcategory: null,
         });

         return true;
      },
      valueValidator: () => ({ isValid: true, isRequired: true }),
      onCellValueChanged: async (params) => {
         const { rowData, newValue } = params;
         // If category is cleared, also clear subcategory
         if (!newValue) {
            Object.assign(rowData, {
               label_name: null,
               subcategory_id: null,
               subcategory: null,
            });
         }
         await updateAssignment(params);
      },
   };

   const subCategoryColumn: SelectCellColumnDefinition = {
      field: "label_name",
      cellRenderer: SelectCellRenderer,
      cellRendererParams: {
         tooltipParams: ({ data }: { data: any }) => {
            if (!data.cost_code_name?.label) {
               return {
                  trigger: "hover",
                  showDelay: 400,
                  placement: "top",
                  hideDelay: 1000,
                  overlay: I18n.t(
                     "views.company.workforce_planning.assignment.validations.subcategory_check",
                  ),
               };
            }
         },
      },
      headerName: I18n.t("views.company.workforce_planning.subcategory"),
      editable: (data) => {
         const subCategoryOptions = formatOptions(
            data.data.subCategoryOptions[data.data.category_id],
         );
         return subCategoryOptions?.length > 0;
      },
      minWidth: 200,
      cellEditor: SelectCellEditor,
      cellEditorParams: {
         selectOptions: async (data: any) => {
            const subCategoryOptions = formatOptions(data.subCategoryOptions[data.category_id]);
            return subCategoryOptions;
         },
         getId: (item: { id: any }) => item?.id,
         clearable: true,
      },
      getStringFormattedValue: (value) => value?.label ?? "",
      valueValidator: () => ({ isValid: true, isRequired: true }),
      onCellValueChanged: async (params) => {
         await updateAssignment(params);
      },
   };

   const workDaysColumn: TextCellColumnDefinition = {
      field: "work_days",
      cellRenderer: TextCellRenderer,
      headerName: I18n.t("views.company.workforce_planning.work_days"),
      editable: false,
      sortable: false,
      minWidth: 200,
   };

   const addressOneColumn: TextCellColumnDefinition = {
      field: "address_1",
      cellRenderer: TextCellRenderer,
      headerName: `${getProjectString(I18n)} ${I18n.t(
         "views.company.workforce_planning.projects.address",
      )}`,
      sortable: false,
      editable: false,
      columnHeaderParams: {
         headerNode: () => renderProjectHeaderNode(projectIntegratedFields, "address_1"),
      },
      minWidth: 200,
   };

   const addressTwoColumn: TextCellColumnDefinition = {
      field: "address_2",
      cellRenderer: TextCellRenderer,
      headerName: `${getProjectString(I18n)} ${I18n.t(
         "views.company.workforce_planning.projects.address_2",
      )}`,
      sortable: false,
      editable: false,
      columnHeaderParams: {
         headerNode: () => renderProjectHeaderNode(projectIntegratedFields, "address_2"),
      },
      minWidth: 200,
   };

   const cityColumn: TextCellColumnDefinition = {
      field: "city_town",
      cellRenderer: TextCellRenderer,
      headerName: `${getProjectString(I18n)} ${I18n.t(
         "views.company.workforce_planning.projects.city",
      )}`,
      sortable: false,
      editable: false,
      columnHeaderParams: {
         headerNode: () => renderProjectHeaderNode(projectIntegratedFields, "city_town"),
      },
      minWidth: 200,
   };

   const stateColumn: TextCellColumnDefinition = {
      cellRenderer: TextCellRenderer,
      field: "state_province",
      headerName: `${getProjectString(I18n)} ${I18n.t(
         "views.company.workforce_planning.projects.state",
      )}`,
      editable: false,
      sortable: false,
      columnHeaderParams: {
         headerNode: () => renderProjectHeaderNode(projectIntegratedFields, "state_province"),
      },
      minWidth: 200,
   };

   const zipCodeColumn: TextCellColumnDefinition = {
      cellRenderer: TextCellRenderer,
      field: "zipcode",
      headerName: `${getProjectString(I18n)} ${I18n.t(
         "views.company.workforce_planning.projects.postal",
      )}`,
      editable: false,
      sortable: false,
      columnHeaderParams: {
         headerNode: () => renderProjectHeaderNode(projectIntegratedFields, "zipcode"),
      },
      minWidth: 200,
   };

   const countryColumn: TextCellColumnDefinition = {
      field: "country",
      cellRenderer: TextCellRenderer,
      headerName: `${getProjectString(I18n)} ${I18n.t(
         "views.company.workforce_planning.projects.country",
      )}`,
      editable: false,
      sortable: false,
      columnHeaderParams: {
         headerNode: () => renderProjectHeaderNode(projectIntegratedFields, "country"),
      },
      minWidth: 200,
   };

   const employeeNumberColumn: TextCellColumnDefinition = {
      field: "employee_number",
      cellRenderer: TextCellRenderer,
      headerName: I18n.t("views.company.workforce_planning.people.employee_id"),
      editable: false,
      sortable: false,
      filterRenderer: TextFilter as any,
      filterProps: {
         index: 6,
      },
      columnHeaderParams: {
         headerNode: () => renderPeopleHeaderNode(peopleIntegratedFields, "employee_number"),
      },
      minWidth: 200,
   };

   const genderColumn: ColumnDefinition = {
      field: "gender",
      cellRenderer: SelectCellRenderer,
      headerName: I18n.t("views.company.workforce_planning.people.gender"),
      sortable: false,
      getStringFormattedValue: (value) => {
         if (!value) return "";
         return value.id === "male"
            ? I18n.t("views.company.workforce_planning.people.male")
            : I18n.t("views.company.workforce_planning.people.female");
      },
      editable: false,
      columnHeaderParams: {
         headerNode: () => renderPeopleHeaderNode(peopleIntegratedFields, "gender"),
      },
      minWidth: 200,
   };

   // this column will be hidden from data-table only visible in filter section
   const personStatusColumn: ColumnDefinition = {
      field: "person_status",
      hidden: true,
      lockVisible: true,
      filterRenderer: StatusFilter,
      filterProps: {
         getFilterHeadingText: () => I18n.t("views.company.workforce_planning.person_status"),
         getFilterOptions: () => PEOPLE_STATUS_OPTIONS,
         getFilterTokenText: (item: any) => {
            const text = I18n.t("views.company.workforce_planning.person_status") + ": ";
            const status: string[] = item.value.map((v: any) => {
               return I18n.t("views.company.workforce_planning." + v.value);
            });
            return text + status.join(", ");
         },
         getLabel: (item: FormattedOption) => item.label,
         index: 10,
      },
      minWidth: 200,
   };

   // this column will be hidden from data-table only visible in filter section
   const projectStatusColumn: ColumnDefinition = {
      field: "project_status",
      hidden: true,
      lockVisible: true,
      filterRenderer: StatusFilter,
      filterProps: {
         getFilterHeadingText: () => I18n.t("views.company.workforce_planning.project_status"),
         getFilterOptions: () => PROJECT_STATUS_OPTIONS,
         getFilterTokenText: (item: any) => {
            const text = I18n.t("views.company.workforce_planning.project_status") + ": ";
            const status: string[] = item.value.map((v: any) => {
               return I18n.t("views.company.workforce_planning." + v.value);
            });
            return text + status.join(", ");
         },
         getLabel: (item: FormattedOption) => item.label,
         index: 9,
      },
      minWidth: 200,
   };

   // this column will be hidden from data-table only visible in filter section
   const allocationTypeColumn: ColumnDefinition = {
      field: "allocation_type",
      hidden: true,
      lockVisible: true,
      filterRenderer: CheckboxFilterRenderer,
      filterProps: {
         getFilterHeadingText: () => I18n.t("views.company.workforce_planning.allocation_type"),
         getFilterOptions: () => ALLOCATION_TYPE_OPTIONS,
         getFilterTokenText: (item: any) => {
            const text = I18n.t("views.company.workforce_planning.allocation_type") + ": ";

            const allocationTypes: string[] = item.value.map((v: any) =>
               I18n.t("views.company.workforce_planning." + v.value),
            );

            return text + allocationTypes.join(", ");
         },
         getLabel: (item: FormattedOption) => item.label,
         index: 11,
      },
      minWidth: 200,
   };

   // this column will be hidden from data-table only visible in filter section
   const projectRolesColumn: ColumnDefinition = {
      field: "project_roles",
      hidden: true,
      lockVisible: true,
      filterRenderer: ProjectRolesFilter as any,
      filterProps: {
         getFilterHeadingText: () =>
            I18n.t("views.company.workforce_planning.project_details_roles.title"),
         getFilterTokenText: (item: any) => {
            const projectRolesPrefix = I18n.t(
               "views.company.workforce_planning.project_details_roles.title",
            );
            return `${projectRolesPrefix} (${item.value.length})`;
         },
         getLabel: (item: FormattedOption) => item.label,
         index: 11,
      },
      minWidth: 200,
   };

   const projectStageColumn: ColumnDefinition = {
      field: "cornerstone_stage",
      headerName: I18n.t("views.company.workforce_planning.assignment.project_stage"),
      editable: false,
      filterRenderer: MultiSelectFilterRenderer,
      filterProps: {
         getFilterOptions: () => {
            return projectStages;
         },
      },
      getStringFormattedValue: (
         value: {
            id: string;
            name: string;
         } | null,
      ) => {
         return value ? value.name : "";
      },
      cellRenderer: TextCellRenderer,
      sortable: true,
   };

   const assignmentColumnDefinitions: ColumnDefinition[] = [
      projectSearchColumn,
      projectNameColumn,
      peopleNameColumn,
      projectNumberColumn,
      jobTitleColumn,
      startDateColumn,
      endDateColumn,
      startTimeColumn,
      endTimeColumn,
      percentAllocatedColumn,
      statusColumn,
      overtimeColumn,
      categoryColumn,
      subCategoryColumn,
      workDaysColumn,
      addressOneColumn,
      addressTwoColumn,
      cityColumn,
      stateColumn,
      zipCodeColumn,
      countryColumn,
      employeeNumberColumn,
      genderColumn,
      personStatusColumn,
      projectStatusColumn,
      allocationTypeColumn,
      projectRolesColumn,
      projectStageColumn,
   ].concat(
      canViewProjectRoles ? generateProjectRoleColumnDefinitions(roleOptions, tableConfig) : [],
   );

   useEffect(() => {
      if (customFields) {
         // Group customFields by entity type (Assignment, Project, People)
         const fieldsByEntity = customFields.reduce((acc: Record<string, any[]>, field) => {
            const entity = field.entity!;
            if (!acc[entity]) {
               acc[entity] = [];
            }
            acc[entity].push(field);
            return acc;
         }, {});

         // Temporary array to store column definitions
         let tempColumnDefinitions: ColumnDefinition[] = [];

         // looping through the formed object to create custom column definition for each entity type
         Object.entries(fieldsByEntity).forEach(([entity, fields]) => {
            let sensitiveFieldsDetails;
            let canEditDetails = false; // keeping it default to false, on legacy we don't allow editing people/project custom fields
            let integratedFields: IntegratedField[] = []; // keeping it default to an empty array as there is no integrated fields for assignment as of now
            let canViewFinancials = false;

            switch (entity) {
               case EntityTypes.ASSIGNMENT:
                  sensitiveFieldsDetails = {
                     canViewSensitiveFields: true, // for assignments there is no permission as of now
                     canEditSensitiveFields: true, // for assignments there is no permission as of now
                     sensitiveFields: [],
                  };
                  canEditDetails = canManageAssignment;
                  canViewFinancials = true;
                  break;
               case EntityTypes.PEOPLE:
                  sensitiveFieldsDetails = {
                     canViewSensitiveFields: canViewPeopleSensitiveFields,
                     canEditSensitiveFields: false, // on legacy we don't allow editing people custom fields
                     sensitiveFields: peopleSensitiveFields,
                  };
                  integratedFields = peopleIntegratedFields;
                  canViewFinancials = canViewPeopleFinancials;
                  break;
               case EntityTypes.PROJECT:
                  sensitiveFieldsDetails = {
                     canViewSensitiveFields: canViewProjectSensitiveFields,
                     canEditSensitiveFields: false, // on legacy we don't allow editing project custom fields
                     sensitiveFields: projectSensitiveFields,
                  };
                  canViewFinancials = canViewProjectFinancials;
                  integratedFields = projectIntegratedFields;
                  break;
               default:
                  sensitiveFieldsDetails = {
                     canViewSensitiveFields: false,
                     canEditSensitiveFields: false,
                     sensitiveFields: [],
                  };
                  integratedFields = [];
                  break;
            }
            // Generate column definitions for the current entity
            const customColumnDefinitions = createColumnDefinitionsFromCustomHeaders(
               fields,
               tableConfig,
               integratedFields,
               null,
               sensitiveFieldsDetails,
               canViewFinancials,
               canEditDetails,
            );
            // Append to temporary array
            if (customColumnDefinitions.length > 0) {
               tempColumnDefinitions = [...tempColumnDefinitions, ...customColumnDefinitions];
            }
         });
         setCustomColumnDefinitions(tempColumnDefinitions);
      }
   }, [
      customFields,
      groupId,
      canManageAssignment,
      canViewPeopleSensitiveFields,
      canViewProjectSensitiveFields,
      canViewPeopleFinancials,
      canViewProjectFinancials,
      peopleSensitiveFields,
      projectSensitiveFields,
      peopleIntegratedFields,
      projectIntegratedFields,
      refreshCustomFields,
   ]);

   //Reorder the columns based on the saved column state to maintain column order
   const reorderedProjectColumnDefinitions = useMemo(
      () =>
         getReorderedColumnDefinition(
            customColumnDefinitions
               ? assignmentColumnDefinitions.concat(customColumnDefinitions)
               : assignmentColumnDefinitions,
            tableConfig.columnState,
         ),
      [customColumnDefinitions, assignmentColumnDefinitions, tableConfig.columnState],
   );

   const handleServerSideDataRequest = useCallback(
      async ({ request, onSuccess, onError }: ServerSideGetRowsParams) => {
         try {
            let startingAfter = undefined;
            // If requesting a second page of data, apply the starting_after query param
            if (request && request.startRow && request.startRow > 0) {
               startingAfter = nextStartAfter.current;
            }
            const sortModel: SortModel[] = request.sortModel;
            const serverFilter: AssignmentListServerFilter[] = request.serverFilters;
            if (customFields) {
               // created a common function to update the custom fields in the filter and sort model
               updateCustomFieldItems(sortModel, customFields);
               updateCustomFieldItems(serverFilter, customFields, true);
            }

            const data = await fetchAssignmentList(serverFilter, sortModel, startingAfter);
            onSuccess({ rowData: data.data, rowCount: data.pagination.total_possible });
            nextStartAfter.current = data.pagination.next_starting_after;

            // Clear selections when filters change (after table has new data)
            if (haveFiltersChanged(serverFilter, previousFiltersRef.current)) {
               setManuallySelectedRowData([]);
               tableApi?.deselectAll();
               previousFiltersRef.current = serverFilter;
            }
         } catch (err) {
            onError();
         }
      },
      [groupId, tableApi],
   );

   return (
      (!integratedFieldsIsLoading && (
         <ServerSideDataTable
            columnDefinitions={reorderedProjectColumnDefinitions}
            onServerSideDataRequest={handleServerSideDataRequest}
            getRowId={(params) => params.data.id}
            onTableConfigChange={onTableConfigChange}
            initialTableConfig={tableConfig}
            key={dataTableKey}
         >
            <Flex
               style={{
                  height: "100%",
                  width: "100%",
               }}
               alignItems="stretch"
            >
               <Flex flex="0 1 0px" alignItems="stretch" paddingRight="md">
                  <ServerSideDataTable.FiltersPanel />
               </Flex>

               <Box flex="1" display="flex-column" alignItems="stretch">
                  <ServerSideDataTable.QuickControls>
                     <Flex>
                        <FlexList size="xs" alignItems="center">
                           <ServerSideDataTable.FiltersPanelButton />
                        </FlexList>
                        <DataTableToolbarQuickFilters />
                        <FlexList size="xs" alignItems="center" style={{ marginLeft: "auto" }}>
                           <AssignmentListQuickControls
                              configKey={
                                 savedView ? LOCAL_STORAGE_SAVED_VIEW_KEY : LOCAL_STORAGE_CONFIG_KEY
                              }
                              groupId={groupId}
                              customFields={customFields ?? []}
                              isLastNameFirst={isLastNameFirst}
                              roleOptions={roleOptions}
                              onRefreshCustomFields={() =>
                                 setRefreshCustomFields(!refreshCustomFields)
                              }
                           />
                           <CreateSavedViewModal
                              configKey={
                                 savedView ? LOCAL_STORAGE_SAVED_VIEW_KEY : LOCAL_STORAGE_CONFIG_KEY
                              }
                              tableApi={tableApi}
                              conversionFunction={convertDataTableConfigToSavedView}
                              page={SavedViewPage.ASSIGNMENTS_LIST}
                              customFields={customFields ?? []}
                              columnHeadersMap={columnHeadersMap}
                              filterNameMaps={filterNameMaps}
                              filterFieldMap={filterFieldMaps}
                              filterRendererMap={filterRendererMap}
                              groupId={groupId}
                           />
                           <ServerSideDataTable.ConfigPanelButton />
                        </FlexList>
                     </Flex>
                  </ServerSideDataTable.QuickControls>
                  <BulkActionsBar
                     selectedRows={manuallySelectedRowData}
                     selectionInProgress={selectionInProgress}
                  >
                     {canManageAssignment && (
                        <Flex alignItems="center" gap="sm">
                           <BulkEditAssignmentTearsheet
                              tableApi={tableApi}
                              customFields={assignmentCustomFields ?? []}
                              selectedRows={manuallySelectedRowData}
                              selectionInProgress={selectionInProgress}
                              setManuallySelectedRowData={setManuallySelectedRowData}
                           />
                           <ConfirmDeleteAssignmentModal
                              tableApi={tableApi}
                              headerText="views.company.workforce_planning.assignment.bulk.delete.header_text"
                              deleteText="views.company.workforce_planning.assignment.bulk.delete.confirmation_modal_text"
                              successText="views.company.workforce_planning.assignment.bulk.delete.success"
                              errorText="views.company.workforce_planning.assignment.bulk.delete.error"
                              highlightedText="views.company.workforce_planning.assignment.bulk.delete.confirmation_modal_highlighted_text"
                              selectedRows={manuallySelectedRowData}
                              selectionInProgress={selectionInProgress}
                              setManuallySelectedRowData={setManuallySelectedRowData}
                           />
                           <AssignmentListSendAlert
                              tableApi={tableApi}
                              selectedRows={manuallySelectedRowData}
                              selectionInProgress={selectionInProgress}
                              setManuallySelectedRowData={setManuallySelectedRowData}
                           />
                        </Flex>
                     )}
                  </BulkActionsBar>
                  <ServerSideDataTable.Table
                     onTableReady={handleTableReady}
                     paginationPageSize={100}
                     rowSelectionEnabled={(node) => {
                        // this will only show checkboxes for labor assignments
                        if (canManageAssignment && node?.data) {
                           return node.data.assignment_type === ResourceType.LABOR;
                        }
                        return false;
                     }}
                     selectionSSREnabled={canManageAssignment}
                     rowActions={canManageAssignment ? [Delete, Edit] : []}
                     suppressColumnVirtualisation={false}
                     rowActionsConfig={{ sortable: false }}
                     onRowSelected={(event) => {
                        // Skip if this is from API select all
                        if (event?.source === "apiSelectAll") {
                           return;
                        }

                        if (event.node.isSelected()) {
                           setManuallySelectedRowData((prev: any) => [...prev, event.node.data]);
                        } else {
                           setManuallySelectedRowData((prev: any) =>
                              prev.filter(
                                 (assignment: any) => assignment.id !== event.node.data.id,
                              ),
                           );
                        }
                     }}
                     onSelectAll={async (params) => {
                        if (params === "all") {
                           setSelectionInProgress(true);
                           try {
                              const filters = savedView
                                 ? JSON.parse(
                                      localStorage.getItem(LOCAL_STORAGE_SAVED_VIEW_KEY) ?? "{}",
                                   )
                                 : JSON.parse(
                                      localStorage.getItem(LOCAL_STORAGE_CONFIG_KEY) ?? "{}",
                                   );
                              const coreApiFilters = filters.serverFilters
                                 ? convertAssignmentDataTableFilters(filters.serverFilters)
                                 : [];
                              const data = [];
                              const stream = await Assignment2Store.getCombinedAssignmentsStream({
                                 filters: coreApiFilters,
                                 starting_after: undefined,
                                 group_id: groupId === "my-groups" ? undefined : groupId,
                                 timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                              }).stream;
                              for await (const assignment of stream) {
                                 // Check if the assignment is of type LABOR, we will start supporting equipment later
                                 if (assignment.assignment_type === ResourceType.LABOR) {
                                    data.push(assignment);
                                 }
                              }
                              const dataTableRecords = mapApiAssignmentToTableAssignment(
                                 data,
                                 I18n,
                                 assignmentCreationSupportData,
                              );
                              setManuallySelectedRowData(dataTableRecords);
                              setSelectionInProgress(false);
                              setRefreshCustomFields(true);
                           } catch (error) {
                              showToast.error(
                                 I18n.t(
                                    "views.company.workforce_planning.data_table.bulk_actions.error",
                                 ),
                              );
                              setManuallySelectedRowData([]);
                              setSelectionInProgress(false);
                              tableApi?.deselectAll();
                              setRefreshCustomFields(false);
                           }
                        } else {
                           setManuallySelectedRowData([]);
                           setRefreshCustomFields(false);
                           setSelectionInProgress(false);
                        }
                     }}
                     onCellValueChanged={async (params) => {
                        if (params.newValue == null) return;

                        await updateAssignment(params);
                     }}
                  />
               </Box>
               <Flex flex="0 1 0px" alignItems="stretch" paddingLeft="md">
                  <ServerSideDataTable.ContextPanel />
               </Flex>
            </Flex>
         </ServerSideDataTable>
      )) ||
      null
   );
};
